import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReplyIcon from '@mui/icons-material/Reply';
import ForwardIcon from '@mui/icons-material/Forward';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CodeIcon from '@mui/icons-material/Code';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { format, parseISO } from 'date-fns';
import {
  getLiveMessage,
  getLiveMessageRaw,
  liveArchiveMessage,
  liveCopyMessage,
  liveMarkFlags,
  liveMoveMessage,
  liveTrashMessage,
} from '../api/mailbox';
import FilterRuleDialog from '../components/FilterRuleDialog';
import EmailHtmlFrame from '../components/EmailHtmlFrame';
import FolderTransferDialog from '../components/FolderTransferDialog';
import MessageContextMenu from '../components/MessageContextMenu';
import MessageAttachments from '../components/MessageAttachments';
import AddSenderContactButton from '../components/AddSenderContactButton';
import MessagePgpActions from '../components/MessagePgpActions';
import { decodeFolderKey } from '../utils/imapPaths';

interface LiveMessagePageProps {
  accountId?: number;
  imapFolder?: string;
  uid?: number;
  embedded?: boolean;
  hideBack?: boolean;
}

export default function LiveMessagePage(props: LiveMessagePageProps = {}) {
  const params = useParams<{ accountId: string; folderKey: string; uid: string }>();
  const accountId = props.accountId ?? parseInt(params.accountId!, 10);
  const imapFolder = props.imapFolder ?? decodeFolderKey(params.folderKey!);
  const uid = props.uid ?? parseInt(params.uid!, 10);
  const embedded = props.embedded;
  const hideBack = props.hideBack;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [viewHtml, setViewHtml] = useState(true);
  const [rawOpen, setRawOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [transferMode, setTransferMode] = useState<'move' | 'copy' | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  const key = { account: accountId, folder: imapFolder, uid };

  const { data: msg, isLoading, isError } = useQuery({
    queryKey: ['live-message', accountId, imapFolder, uid],
    queryFn: () => getLiveMessage(key),
    enabled: Boolean(accountId && imapFolder && uid),
  });

  const { data: rawData, isFetching: rawLoading } = useQuery({
    queryKey: ['live-message-raw', accountId, imapFolder, uid],
    queryFn: () => getLiveMessageRaw(key),
    enabled: rawOpen && Boolean(uid),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['live-message', accountId, imapFolder, uid] });
    queryClient.invalidateQueries({ queryKey: ['live-threads'] });
    queryClient.invalidateQueries({ queryKey: ['live-thread'] });
    queryClient.invalidateQueries({ queryKey: ['live-folders'] });
  };

  const starMutation = useMutation({
    mutationFn: (starred: boolean) => liveMarkFlags({ ...key, starred }),
    onSuccess: invalidate,
  });

  const trashMutation = useMutation({
    mutationFn: () => liveTrashMessage(key),
    onSuccess: () => {
      invalidate();
      if (!embedded) navigate(-1);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => liveArchiveMessage(key),
    onSuccess: () => {
      invalidate();
      if (!embedded) navigate(-1);
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError || !msg) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load message from IMAP.
      </Alert>
    );
  }

  const hasBody = Boolean(msg.body_html || msg.body_text);

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, maxWidth: embedded ? '100%' : 900, mx: 'auto' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {!hideBack && (
          <Tooltip title="Back">
            <IconButton onClick={() => navigate(-1)} aria-label="Back">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Reply">
          <IconButton
            onClick={() =>
              navigate(
                `/compose?replyAccount=${accountId}&replyFolder=${encodeURIComponent(imapFolder)}&replyUid=${uid}`
              )
            }
            aria-label="Reply"
          >
            <ReplyIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Forward">
          <IconButton
            onClick={() =>
              navigate(
                `/compose?forwardAccount=${accountId}&forwardFolder=${encodeURIComponent(imapFolder)}&forwardUid=${uid}`
              )
            }
            aria-label="Forward"
          >
            <ForwardIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={msg.is_starred ? 'Unstar' : 'Star'}>
          <IconButton
            onClick={() => starMutation.mutate(!msg.is_starred)}
            aria-label={msg.is_starred ? 'Unstar' : 'Star'}
          >
            {msg.is_starred ? <StarIcon color="warning" /> : <StarBorderIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Archive">
          <IconButton onClick={() => archiveMutation.mutate()} aria-label="Archive">
            <ArchiveIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton onClick={() => trashMutation.mutate()} aria-label="Delete">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="View raw source">
          <IconButton onClick={() => setRawOpen(true)} aria-label="View raw source">
            <CodeIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Move">
          <IconButton onClick={() => setTransferMode('move')} aria-label="Move">
            <DriveFileMoveIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy">
          <IconButton onClick={() => setTransferMode('copy')} aria-label="Copy">
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
        <Button size="small" variant="outlined" startIcon={<FilterAltIcon />} onClick={() => setFilterOpen(true)}>
          Filter
        </Button>
      </Stack>

      <Paper
        sx={{ p: { xs: 2, md: 3 } }}
        elevation={1}
        onContextMenu={(e) => {
          e.preventDefault();
          setMenuPos({ top: e.clientY, left: e.clientX });
        }}
      >
        <Typography variant="h6" gutterBottom fontWeight={600}>
          {msg.subject || '(no subject)'}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <b>From:</b> {msg.from_address}
          </Typography>
          <AddSenderContactButton fromAddress={msg.from_address} />
          <Typography variant="body2" color="text.secondary">
            <b>To:</b> {msg.to_addresses}
          </Typography>
          {msg.cc_addresses && (
            <Typography variant="body2" color="text.secondary">
              <b>Cc:</b> {msg.cc_addresses}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            <b>Date:</b> {msg.date ? format(parseISO(msg.date), 'PPpp') : 'Unknown'}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <MessagePgpActions bodyText={msg.body_text} bodyHtml={msg.body_html} />
        {msg.body_html && msg.body_text && (
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Button size="small" variant={viewHtml ? 'contained' : 'outlined'} onClick={() => setViewHtml(true)}>
              HTML
            </Button>
            <Button size="small" variant={!viewHtml ? 'contained' : 'outlined'} onClick={() => setViewHtml(false)}>
              Plain text
            </Button>
          </Stack>
        )}
        {!hasBody ? (
          <Alert severity="info">No message body was returned from IMAP.</Alert>
        ) : viewHtml && msg.body_html ? (
          <EmailHtmlFrame html={msg.body_html} title={msg.subject || 'Email'} />
        ) : (
          <Box component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {msg.body_text}
          </Box>
        )}
        {msg.attachments && msg.attachments.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <MessageAttachments
              attachments={msg.attachments}
              live={{ account: accountId, folder: imapFolder, uid }}
            />
          </>
        )}
      </Paper>

      <Dialog open={rawOpen} onClose={() => setRawOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Raw email source</DialogTitle>
        <DialogContent>
          {rawLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              component="pre"
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                m: 0,
              }}
            >
              {rawData?.raw || msg.raw || '(empty)'}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRawOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <FilterRuleDialog open={filterOpen} onClose={() => setFilterOpen(false)} message={msg} />

      <FolderTransferDialog
        open={transferMode !== null}
        mode={transferMode || 'move'}
        excludeImapFolder={imapFolder}
        onClose={() => setTransferMode(null)}
        onSelectLive={async (dest) => {
          const payload = {
            account: accountId,
            folder: imapFolder,
            uid,
            dest_folder: dest.imap_name,
            dest_account: dest.account,
          };
          if (transferMode === 'copy') await liveCopyMessage(payload);
          else {
            await liveMoveMessage(payload);
            if (!embedded) navigate(-1);
          }
          invalidate();
        }}
      />

      <MessageContextMenu
        anchorPosition={menuPos}
        message={{
          account: accountId,
          imap_folder: imapFolder,
          uid,
          from_address: msg.from_address,
          to_addresses: msg.to_addresses,
          cc_addresses: msg.cc_addresses,
          subject: msg.subject,
        }}
        onClose={() => setMenuPos(null)}
        afterLeave={() => {
          if (!embedded) navigate(-1);
        }}
      />
    </Box>
  );
}
